import React, { useEffect, useState } from 'react';
import { Edit, Trash2, X, ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import AddInfrastructureModal from './AddInfrastructureModal';
import { useSelector } from 'react-redux';

function NearestInfrastructure_detail({ projectiondataID }) {
  const [infraData, setInfraData] = useState([]);
  const [locations, setLocations] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentInfra, setCurrentInfra] = useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [infraToDelete, setInfraToDelete] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);

  const { data, isError, isSuccess } = useSelector((s) => s.GetTransportDetail); 

  useEffect(() => {
    if (isSuccess && data) {
      setLocations(data.data?.nearest_infrastructure || []);
      setCurrentPage(1);
    }
  }, [isSuccess, data]);

  const openAddModal = () => {
    setCurrentInfra(null);
    setIsModalOpen(true);
  };

  const openEditModal = (infra) => {
    setCurrentInfra(infra);
    setIsModalOpen(true);
  };

  const handleSubmit = (formData) => {
    if (currentInfra) {
      setInfraData(infraData.map(infra =>
        infra.id === currentInfra.id ? { ...formData, id: currentInfra.id } : infra
      ));
    } else {
      const newInfra = {
        ...formData,
        id: infraData.length > 0 ? Math.max(...infraData.map(i => i.id)) + 1 : 1
      };
      setInfraData([...infraData, newInfra]);
    }
    setCurrentPage(Math.ceil((infraData.length + 1) / itemsPerPage));
  };

  const handleDeleteConfirmation = (infra) => {
    setInfraToDelete(infra);
    setShowDeleteConfirmation(true);
  };

  const handleDelete = () => {
    setInfraData(infraData.filter(infra => infra.id !== infraToDelete.id));
    if (currentPage > 1 && currentItems.length === 1) {
      setCurrentPage(currentPage - 1);
    }
    setShowDeleteConfirmation(false);
  };

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = locations.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(locations.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <div className="p-4 bg-gray-800 rounded-lg">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-semibold">Nearest Infrastructure</h3>
        <button
          onClick={openAddModal}
          className="p-1.5 bg-blue-600 hover:bg-blue-700 rounded-full border border-blue-500"
          title="Add Infrastructure"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>

      <div className="text-sm border border-gray-700 rounded-lg overflow-hidden">
        {locations.length > 0 ? (
          <>
            <table className="w-full">
              <thead>
                <tr className="bg-gray-750 text-gray-300 border-b border-gray-700">
                  <th className="px-3 py-2 text-left border-r border-gray-700">Name</th>
                  <th className="px-3 py-2 text-left border-r border-gray-700">Type</th>
                  <th className="px-3 py-2 text-left border-r border-gray-700">Distance</th>
                  <th className="px-3 py-2 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.map((infra) => (
                  <tr 
                    key={infra._id} 
                    className="border-b border-gray-700 hover:bg-gray-750 last:border-b-0"
                  >
                    <td className="px-3 py-2 border-r border-gray-700 text-left">{infra.name}</td>
                    <td className="px-3 py-2 border-r border-gray-700 text-left">{infra.type}</td>
                    <td className="px-3 py-2 border-r border-gray-700 text-left">{infra.distance}</td>
                    <td className="px-3 py-2">
                      <div className="flex space-x-2">
                        <button 
                          onClick={() => openEditModal(infra)}
                          className="text-blue-400 hover:text-blue-300"
                        >
                          <Edit className="w-3.5 h-3.5" />
                        </button>
                        <button 
                          onClick={() => handleDeleteConfirmation(infra)}
                          className="text-red-400 hover:text-red-300"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination controls */}
            <div className="flex items-center justify-between px-3 py-2 bg-gray-750 border-t border-gray-700">
              <div className="text-xs text-gray-400">
                Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, locations.length)} of {locations.length}
              </div>
              <div className="flex space-x-1">
                <button
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`p-1.5 rounded border ${currentPage === 1 ? 'bg-gray-700 border-gray-600' : 'bg-gray-600 border-gray-500 hover:bg-gray-550'}`}
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                </button>
                
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(number => (
                  <button
                    key={number}
                    onClick={() => paginate(number)}
                    className={`p-1.5 px-2.5 rounded border ${currentPage === number ? 'bg-blue-600 border-blue-500' : 'bg-gray-600 border-gray-500 hover:bg-gray-550'}`}
                  >
                    {number}
                  </button>
                ))}
                
                <button
                  onClick={() => paginate(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`p-1.5 rounded border ${currentPage === totalPages ? 'bg-gray-700 border-gray-600' : 'bg-gray-600 border-gray-500 hover:bg-gray-550'}`}
                >
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-4 text-gray-400">
            <button
              onClick={openAddModal}
              className="text-blue-400 hover:text-blue-300 text-sm"
            >
              + Add Infrastructure
            </button>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-4 w-full max-w-xs border border-gray-700">
            <div className="flex justify-between items-center mb-3 border-b border-gray-700 pb-2">
              <h3 className="font-medium">Confirm Deletion</h3>
              <button onClick={() => setShowDeleteConfirmation(false)} className="text-gray-400">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-gray-300 mb-4">
              Delete this infrastructure detail?
            </p>
            <div className="flex justify-end space-x-2 border-t border-gray-700 pt-3">
              <button
                onClick={() => setShowDeleteConfirmation(false)}
                className="px-3 py-1.5 text-sm bg-gray-600 hover:bg-gray-700 rounded border border-gray-500"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-3 py-1.5 text-sm bg-red-600 hover:bg-red-700 rounded flex items-center border border-red-500"
              >
                <Trash2 className="w-3.5 h-3.5 mr-1" />
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <AddInfrastructureModal
        isOpen={isModalOpen}
        projectiondataID={projectiondataID}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleSubmit}
        infrastructureToEdit={currentInfra}
      />
    </div>
  );
}

export default NearestInfrastructure_detail;

// import React, { useEffect, useState } from 'react';
// import { FaPlus, FaEdit, FaTrash } from 'react-icons/fa';
// import AddInfrastructureModal from './AddInfrastructureModal';
// // import { GetTransportDetail } from '../../../../FeatureRedux/ProjectionReducer/TransportProjectionReducer/getTransport_Projection_reducre';
// import { useSelector } from 'react-redux';

// function NearestInfrastructure_detail({projectiondataID}) {
//   const [infraData, setInfraData] = useState([

//   ]); 
//     const [locations, setLocations] = useState([]);

//   const [isModalOpen, setIsModalOpen] = useState(false);
//   const [currentInfra, setCurrentInfra] = useState(null);
//   const [currentPage, setCurrentPage] = useState(1);  
//   const itemsPerPage = 3;
//   const { data, isError, isSuccess } = useSelector((s) => s.GetTransportDetail); 

// useEffect(() => {
//   // console.log("Data of transport and infra",data)
//     if (isSuccess && data) {
//       setLocations(data.data?.nearest_infrastructure || []);
//       setCurrentPage(1); // Reset to first page when data changes
//     }
//   }, [isSuccess, data]);

//   const openAddModal = () => {
//     setCurrentInfra(null);
//     setIsModalOpen(true);
//   };

//   const openEditModal = (infra) => {
//     setCurrentInfra(infra);
//     setIsModalOpen(true);
//   };

//   const handleSubmit = (formData) => {
//     if (currentInfra) {
//       setInfraData(infraData.map(infra =>
//         infra.id === currentInfra.id ? { ...formData, id: currentInfra.id } : infra
//       ));
//     } else {
//       const newInfra = {
//         ...formData,
//         id: infraData.length > 0 ? Math.max(...infraData.map(i => i.id)) + 1 : 1
//       };
//       setInfraData([...infraData, newInfra]);
//     }
//   };

//   const handleDelete = (id) => {
//     setInfraData(infraData.filter(infra => infra.id !== id));
//   };

//   const totalPages = Math.ceil(infraData.length / itemsPerPage);
//   const paginatedData = infraData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

//   return (
//     <div className="p-4 text-white w-full max-w-5xl mx-auto">
//       <div className="relative flex items-center mb-4">
//         <h3 className="text-xl font-semibold absolute left-1/2 transform -translate-x-1/2">
//           Nearest Infrastructure
//         </h3>
//         <button
//           onClick={openAddModal}
//           className="ml-auto bg-blue-500 hover:bg-blue-600 text-white p-2 rounded-full"
//           title="Add Infrastructure"
//         >
//           <FaPlus />
//         </button>
//       </div>

//       <div className="overflow-x-auto bg-gray-800 rounded-lg shadow-md">
//         <table className="min-w-full border border-gray-600 text-sm">
//           <thead className="bg-gray-800 text-gray-200">
//             <tr>
//               <th className="border border-gray-600 px-4 py-2 text-left">Name</th>
//               <th className="border border-gray-600 px-4 py-2 text-left">Type</th>
//               <th className="border border-gray-600 px-4 py-2 text-left">Distance</th>
//               {/* <th className="border border-gray-600 px-4 py-2 text-left">Direction</th> */}
//               <th className="border border-gray-600 px-4 py-2 text-left">Actions</th>
//             </tr>
//           </thead>
//           <tbody>
//             {locations && locations.map((infra) => (
//               <tr key={infra._id} className="hover:bg-gray-700">
//                 <td className="border border-gray-600 px-4 py-2 text-left">{infra.name}</td>
//                 <td className="border border-gray-600 px-4 py-2 text-left">{infra.type}</td>
//                 <td className="border border-gray-600 px-4 py-2 text-left">{infra.distance}</td>
//                 {/* <td className="border border-gray-600 px-4 py-2">{infra.direction}</td> */}
//                 <td className="border border-gray-600 px-4 py-2 space-x-2">
//                   <button
//                     onClick={() => openEditModal(infra)}
//                     className="text-blue-500 hover:text-blue-700"
//                     title="Edit"
//                   >
//                     <FaEdit className="w-3 h-3" />
//                   </button>
//                   <button
//                     onClick={() => handleDelete(infra.id)}
//                     className="text-red-500 hover:text-red-700"
//                     title="Delete"
//                   >
//                     <FaTrash className="w-3 h-3" />
//                   </button>
//                 </td>
//               </tr>
//             ))}
//           </tbody>
//         </table>
//       </div>

//       {/* Pagination */}
//       <div className="flex items-center justify-between w-full mt-4">
//         <div className="text-sm text-gray-400">
//           Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, infraData.length)} of {infraData.length} entries
//         </div>
//         <div className="flex space-x-2">
//           <button
//             onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
//             disabled={currentPage === 1}
//             className={`p-2 rounded-md ${currentPage === 1 ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-600 text-white hover:bg-gray-500'}`}
//           >
//             Prev
//           </button>
          
//           {Array.from({ length: totalPages }).map((_, index) => (
//             <button
//               key={index + 1}
//               onClick={() => setCurrentPage(index + 1)}
//               className={`p-2 px-3 rounded-md ${currentPage === index + 1 ? 'bg-blue-500 text-white' : 'bg-gray-600 text-white hover:bg-gray-500'}`}
//             >
//               {index + 1}
//             </button>
//           ))}

//           <button
//             onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
//             disabled={currentPage === totalPages}
//             className={`p-2 rounded-md ${currentPage === totalPages ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-600 text-white hover:bg-gray-500'}`}
//           >
//             Next
//           </button>
//         </div>
//       </div>

//       <AddInfrastructureModal
//         isOpen={isModalOpen}
//         projectiondataID={projectiondataID}
//         onClose={() => setIsModalOpen(false)}
//         onSubmit={handleSubmit}
//         infrastructureToEdit={currentInfra}
//       />
//     </div>
//   );
// }

// export default NearestInfrastructure_detail;

// import React, { useState } from 'react';
// import { FaPlus, FaEdit, FaTrash } from 'react-icons/fa';
// import AddInfrastructureModal from './AddInfrastructureModal';

// function NearestInfrastructure_detail() {
//   const [infraData, setInfraData] = useState([
//     { id: 1, name: "Central Park", type: "Park", distance: "1.8 km", direction: "North" },
//     { id: 2, name: "City Mall", type: "Mall", distance: "3.2 km", direction: "East" },
//     { id: 3, name: "Shree Balaji Temple", type: "Temple", distance: "2.0 km", direction: "West" },
//     { id: 4, name: "Metro Expansion Project", type: "Govt Project", distance: "0.9 km", direction: "South-East" },
//     { id: 5, name: "Freedom Monument", type: "Famous Place", distance: "4.5 km", direction: "North-West" },
//     { id: 6, name: "Community Hall", type: "Hall", distance: "1.2 km", direction: "North-East" },
//     { id: 7, name: "Green Lake", type: "Lake", distance: "2.7 km", direction: "South-West" },
//   ]);

//   const [isModalOpen, setIsModalOpen] = useState(false);
//   const [currentInfra, setCurrentInfra] = useState(null);
//   const [currentPage, setCurrentPage] = useState(1);  
//   const itemsPerPage = 3;

//   const openAddModal = () => {
//     setCurrentInfra(null);
//     setIsModalOpen(true);
//   };

//   const openEditModal = (infra) => {
//     setCurrentInfra(infra);
//     setIsModalOpen(true);
//   };

//   const handleSubmit = (formData) => {
//     if (currentInfra) {
//       setInfraData(infraData.map(infra =>
//         infra.id === currentInfra.id ? { ...formData, id: currentInfra.id } : infra
//       ));
//     } else {
//       const newInfra = {
//         ...formData,
//         id: infraData.length > 0 ? Math.max(...infraData.map(i => i.id)) + 1 : 1
//       };
//       setInfraData([...infraData, newInfra]);
//     }
//   };

//   const handleDelete = (id) => {
//     setInfraData(infraData.filter(infra => infra.id !== id));
//   };

//   const totalPages = Math.ceil(infraData.length / itemsPerPage);
//   const paginatedData = infraData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

//   return (
//     <div className="p-4 text-white w-full max-w-5xl mx-auto">
//       <div className="flex justify-between items-center mb-4">
//         <h3 className="text-xl font-semibold text-center flex-1">Nearest Infrastructure</h3>
//         <button
//           onClick={openAddModal}
//           className="ml-4 bg-blue-500 hover:bg-blue-600 text-white p-2 rounded-full"
//           title="Add Infrastructure"
//         >
//           <FaPlus />
//         </button>
//       </div>

//       <div className="overflow-x-auto bg-gray-800 rounded-lg shadow-md">
//         <table className="min-w-full border border-gray-600 text-xs">
//           <thead className="bg-gray-800 text-gray-200">
//             <tr>
//               <th className="border border-gray-600 px-2 py-2 text-left">Name</th>
//               <th className="border border-gray-600 px-2 py-2 text-left">Type</th>
//               <th className="border border-gray-600 px-2 py-2 text-left">Distance</th>
//               <th className="border border-gray-600 px-2 py-2 text-left">Direction</th>
//               <th className="border border-gray-600 px-2 py-2 text-left">Actions</th>
//             </tr>
//           </thead>
//           <tbody>
//             {paginatedData.map((infra) => (
//               <tr key={infra.id} className="hover:bg-gray-700">
//                 <td className="border border-gray-600 px-2 py-1">{infra.name}</td>
//                 <td className="border border-gray-600 px-2 py-1">{infra.type}</td>
//                 <td className="border border-gray-600 px-2 py-1">{infra.distance}</td>
//                 <td className="border border-gray-600 px-2 py-1">{infra.direction}</td>
//                 <td className="border border-gray-600 px-2 py-1 space-x-2">
//                   <button
//                     onClick={() => openEditModal(infra)}
//                     className="text-blue-400 hover:text-blue-300"
//                     title="Edit"
//                   >
//                     <FaEdit />
//                   </button>
//                   <button
//                     onClick={() => handleDelete(infra.id)}
//                     className="text-red-400 hover:text-red-300"
//                     title="Delete"
//                   >
//                     <FaTrash />
//                   </button>
//                 </td>
//               </tr>
//             ))}
//           </tbody>
//         </table>
//       </div>

//       {/* Pagination */}
//       <div className="flex justify-center mt-4 space-x-2 text-sm">
//         <button
//           onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
//           disabled={currentPage === 1}
//           className={`px-3 py-1 rounded ${currentPage === 1 ? 'bg-gray-600 cursor-not-allowed' : 'bg-gray-700 hover:bg-gray-600'}`}
//         >
//           Prev
//         </button>
//         {Array.from({ length: totalPages }).map((_, index) => (
//           <button
//             key={index + 1}
//             onClick={() => setCurrentPage(index + 1)}
//             className={`px-3 py-1 rounded ${currentPage === index + 1 ? 'bg-blue-600' : 'bg-gray-700 hover:bg-gray-600'}`}
//           >
//             {index + 1}
//           </button>
//         ))}
//         <button
//           onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
//           disabled={currentPage === totalPages}
//           className={`px-3 py-1 rounded ${currentPage === totalPages ? 'bg-gray-600 cursor-not-allowed' : 'bg-gray-700 hover:bg-gray-600'}`}
//         >
//           Next
//         </button>
//       </div>

//       <AddInfrastructureModal
//         isOpen={isModalOpen}
//         onClose={() => setIsModalOpen(false)}
//         onSubmit={handleSubmit}
//         infrastructureToEdit={currentInfra}
//       />
//     </div>
//   );
// }

// export default NearestInfrastructure_detail;

// import React from 'react'

// function NearestInfrastructure_detail() {
//   const infraData = [
//     { name: "Central Park", type: "Park", distance: "1.8 km", direction: "North" },
//     { name: "City Mall", type: "Mall", distance: "3.2 km", direction: "East" },
//     { name: "Shree Balaji Temple", type: "Temple", distance: "2.0 km", direction: "West" },
//     { name: "Metro Expansion Project", type: "Govt Project", distance: "0.9 km", direction: "South-East" },
//     { name: "Freedom Monument", type: "Famous Place", distance: "4.5 km", direction: "North-West" },
//   ];

//   return (
//     <div className="p-4 text-white">
//       <h2 className="text-xl font-semibold mb-4">Nearest Infrastructure</h2>
//       <table className="w-full border border-gray-600 text-sm">
//         <thead className="bg-gray-800 text-gray-200">
//           <tr>
//             <th className="border border-gray-600 px-4 py-2 text-left">Name</th>
//             <th className="border border-gray-600 px-4 py-2 text-left">Type</th>
//             <th className="border border-gray-600 px-4 py-2 text-left">Distance</th>
//             <th className="border border-gray-600 px-4 py-2 text-left">Direction</th>
//           </tr>
//         </thead>
//         <tbody>
//           {infraData.map((infra, index) => (
//             <tr key={index} className="hover:bg-gray-700">
//               <td className="border border-gray-600 px-4 py-2 text-left">{infra.name}</td>
//               <td className="border border-gray-600 px-4 py-2 text-left">{infra.type}</td>
//               <td className="border border-gray-600 px-4 py-2 text-left">{infra.distance}</td>
//               <td className="border border-gray-600 px-4 py-2 text-left">{infra.direction}</td>
//             </tr>
//           ))}
//         </tbody>
//       </table>
//     </div>
//   )
// }

// export default NearestInfrastructure_detail;